/**
 * ╔══════════════════════════════════════════════════════════════╗
 *                         OPSecours™
 *              Emergency & Rescue Simulation Experience
 * ╠══════════════════════════════════════════════════════════════╣
 *   Developed by : Team OpSecours
 *   Technology   : TypeScript / TSX Web Game Engine
 *   Build Status : Active Development
 *
 *   © 2024 / 2026 OpSecours - Bondi51 & Team OpSecours
 *   All Rights Reserved.
 *
 *   Confidential project. Unauthorized distribution prohibited.
 * ╚══════════════════════════════════════════════════════════════╝
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '../core/store/store';
import { LanguageProvider } from '../shared/contexts/LanguageContext';
import ProtectedRoute from '../shared/components/ProtectedRoute';
import NavigationLoader from '../shared/components/NavigationLoader';
import SessionRestorer from '../shared/components/SessionRestorer';

// Pages publiques (pré-connexion)
import Home from '../features/auth/pages/Home';
import HomeDesktop from '../features/auth/pages/HomeDesktop';
import ResetPassword from '../features/auth/pages/ResetPassword';
import ValidateAccount from '../features/auth/pages/ValidateAccount';
import { isElectron } from '../shared/utils/isElectron';
import { getRouterBasename } from '../core/config/api';

// Pages légales
import MentionsLegales from '../features/legal/pages/MentionsLegales';
import ConditionsUtilisation from '../features/legal/pages/ConditionsUtilisation';
import PolitiqueConfidentialite from '../features/legal/pages/PolitiqueConfidentialite';

// Pages de support
import Aide from '../features/support/pages/Aide';
import FAQ from '../features/support/pages/FAQ';
import Contact from '../features/support/pages/Contact';
import Changelog from '../features/support/pages/Changelog';

// Pages du jeu (post-connexion)
import Dashboard from '../features/game/dashboard/Dashboard';
import AlliancePage from '../features/game/dashboard/pages/AlliancePage';
import Map from '../features/game/Map';
import Panel from '../features/panel/Panel';
import UserRolesManagement from '../features/panel/pages/UserRolesManagement';
import UsersManagement from '../features/panel/pages/UsersManagement';
import RoleDefaultAccessManagement from '../features/panel/pages/RoleDefaultAccessManagement';
import CmManagement from '../features/panel/pages/CmManagement';
import IndividualRoleManagement from '../features/panel/pages/IndividualRoleManagement';
import BackupManagement from '../features/panel/pages/BackupManagement';
import SystemConfiguration from '../features/panel/pages/SystemConfiguration';
import SecurityLogs from '../features/panel/pages/SecurityLogs';
import SystemMaintenance from '../features/panel/pages/SystemMaintenance';
import InterventionsCreate from '../features/panel/pages/InterventionsCreate';
import InterventionsManage from '../features/panel/pages/InterventionsManage';
import CtaCategoriesMissionManagement from '../features/panel/pages/CtaCategoriesMissionManagement';
import PanelSupportTeamPage from '../features/panel/pages/TeamSupport';

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <Router
          basename={getRouterBasename()}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <SessionRestorer />
          <NavigationLoader />
          <Routes>
            {/* Pages publiques */}
            <Route path="/" element={isElectron() ? <HomeDesktop /> : <Home />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/validate" element={<ValidateAccount />} />

            {/* Pages légales */}
            <Route path="/legal/mentions-legales" element={<MentionsLegales />} />
            <Route path="/legal/conditions-utilisation" element={<ConditionsUtilisation />} />
            <Route path="/legal/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            
            {/* Pages de support */}
            <Route path="/support/aide" element={<Aide />} />
            <Route path="/support/faq" element={<FAQ />} />
            <Route path="/support/contact" element={<Contact />} />
            <Route path="/support/changelog" element={<Changelog />} />
            
            {/* Pages protégées (jeu) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/alliance"
              element={
                <ProtectedRoute>
                  <AlliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/game/map"
              element={
                <ProtectedRoute>
                  <Map />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel"
              element={
                <ProtectedRoute>
                  <Panel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/users/roles"
              element={
                <ProtectedRoute>
                  <UserRolesManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/cm"
              element={
                <ProtectedRoute>
                  <CmManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/roles/default-access"
              element={
                <ProtectedRoute>
                  <RoleDefaultAccessManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/roles/individual"
              element={
                <ProtectedRoute>
                  <IndividualRoleManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/users"
              element={
                <ProtectedRoute>
                  <UsersManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/system/backup"
              element={
                <ProtectedRoute>
                  <BackupManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/system/configuration"
              element={
                <ProtectedRoute>
                  <SystemConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/system/maintenance"
              element={
                <ProtectedRoute>
                  <SystemMaintenance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/system/security-logs"
              element={
                <ProtectedRoute>
                  <SecurityLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/create"
              element={
                <ProtectedRoute>
                  <InterventionsCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/edit/:id"
              element={
                <ProtectedRoute>
                  <InterventionsCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/manage"
              element={
                <ProtectedRoute>
                  <InterventionsManage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/cta/categories-mission"
              element={
                <ProtectedRoute>
                  <CtaCategoriesMissionManagement />
                </ProtectedRoute>
              }
            />
            {/* Page tickets équipe : default export de TeamSupport.tsx */}
            <Route
              path="/panel/support/team"
              element={
                <ProtectedRoute>
                  <PanelSupportTeamPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </LanguageProvider>
    </Provider>
  );
};

export default App;

